import React, { createElement, useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';

import {
  setupWebRecaptcha,
  sendPhoneVerificationCode,
  confirmPhoneVerificationCode,
} from '../../services/authService';
import { getAuthErrorMessage } from '../../utils/authErrors';
import type { AuthStackParamList } from '../../navigation/types';

type Props = NativeStackScreenProps<AuthStackParamList, 'PhoneAuth'>;

export function PhoneAuthScreen({ navigation }: Props) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (Platform.OS === 'web') {
      setupWebRecaptcha('recaptcha-container');
    }
  }, []);

  async function sendCode() {
    setError(null);
    setBusy(true);
    try {
      await sendPhoneVerificationCode(phone.trim());
      setStep('code');
    } catch (e: unknown) {
      setError(getAuthErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    setError(null);
    setBusy(true);
    try {
      await confirmPhoneVerificationCode(code.trim());
    } catch (e: unknown) {
      setError(getAuthErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const canSendCode = phone.replace(/\s/g, '').length >= 10;
  const canVerify = code.trim().length >= 4;

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
 className="flex-1 bg-zinc-50 dark:bg-zinc-900">
      {Platform.OS === 'web'
        ? createElement('div', {
            id: 'recaptcha-container',
            style: { height: 0, overflow: 'hidden' },
          })
        : null}
      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1, paddingHorizontal: 24, paddingTop: 48, paddingBottom: 40 }}>
 <Text className="text-3xl font-bold text-gray-800 mb-1">Telefon ile giriş</Text>
 <Text className="text-gray-500 text-base mb-6">
          {step === 'phone'
            ? 'Numaranı ülke koduyla birlikte gir; sana bir SMS kodu göndereceğiz.'
            : 'Telefonuna gelen kodu aşağıya yaz.'}
        </Text>

        {error ? (
 <View className="mb-4 rounded-2xl border border-zinc-300 bg-zinc-100 dark:bg-zinc-900 px-4 py-3">
 <Text className="text-center text-sm text-zinc-800">{error}</Text>
          </View>
        ) : null}

        {step === 'phone' ? (
          <>
 <Text className="text-gray-800 font-medium mb-2">Telefon numarası</Text>
            <TextInput
 className="mb-2 rounded-2xl border border-zinc-200 bg-white px-4 py-3.5 text-base text-gray-800"
              placeholder="+905551234567"
              placeholderTextColor="#9ca3af"
              keyboardType="phone-pad"
              value={phone}
              onChangeText={t => {
                setError(null);
                setPhone(t);
              }}
            />
 <Text className="text-gray-400 text-xs mb-6">
              E.164 formatı: + ve ülke kodu ile başlamalıdır.
            </Text>
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={sendCode}
              disabled={busy || !canSendCode}
              className={`mb-4 rounded-2xl border border-zinc-900 py-4 dark:border-zinc-50 ${
                busy || !canSendCode ? 'bg-zinc-300 dark:bg-zinc-700' : 'bg-zinc-900 dark:bg-zinc-50'
              }`}>
              <Text className="text-center text-base font-semibold text-white dark:text-zinc-950">
                {busy ? 'Gönderiliyor…' : 'Kod gönder'}
              </Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
 <Text className="text-gray-800 font-medium mb-2">SMS kodu</Text>
            <TextInput
 className="mb-6 rounded-2xl border border-zinc-200 bg-white px-4 py-3.5 text-base text-gray-800"
              placeholder="123456"
              placeholderTextColor="#9ca3af"
              keyboardType="number-pad"
              value={code}
              onChangeText={t => {
                setError(null);
                setCode(t);
              }}
            />
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={verify}
              disabled={busy || !canVerify}
              className={`mb-4 rounded-2xl border border-zinc-900 py-4 dark:border-zinc-50 ${
                busy || !canVerify ? 'bg-zinc-300 dark:bg-zinc-700' : 'bg-zinc-900 dark:bg-zinc-50'
              }`}>
              <Text className="text-center text-base font-semibold text-white dark:text-zinc-950">
                {busy ? 'Doğrulanıyor…' : 'Doğrula ve devam et'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={() => {
                setStep('phone');
                setCode('');
                setError(null);
              }}
 className="py-2">
 <Text className="text-center text-sm text-gray-500">Numarayı değiştir</Text>
            </TouchableOpacity>
          </>
        )}

        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => navigation.goBack()}
 className="mt-4 py-3">
 <Text className="text-center text-base text-gray-500">
 <Text className="font-semibold text-zinc-600">Geri</Text> — E-posta ile girişe dön
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}
